import classNames from 'classnames';
import { HTMLAttributes, ReactNode, useEffect } from 'react';
import * as Flowbite from 'flowbite-react';
import { getReplaceThemeProps } from 'utils/replaceTheme';

type FlowbiteModalProps = Pick<
    Flowbite.ModalProps,
    'size' | 'dismissible' | 'onClose' | 'position' | 'theme' | 'popup'
>;

type DialogProps = {
    visible: boolean;
    headerText?: ReactNode;
    footer?: ReactNode;
} & FlowbiteModalProps &
    HTMLAttributes<HTMLDivElement>;

export const modalTheme = Flowbite.createTheme({
    modal: {
        footer: {
            base: 'flex items-center justify-center gap-4 rounded-b border-gray-200 p-4 dark:border-gray-600 border-t',
        },
        header: {
            base: 'flex items-start justify-between rounded-t border-b border-gray-200 p-5 dark:border-gray-600',
            close: {
                base: 'bg-transparent cursor-pointer duration-200 inline-flex items-center ml-auto outline-hidden p-1.5 rounded-lg text-disabled text-sm transition-[background-color,box-shadow,color] focus-visible:bg-gray-900/5 focus-visible:shadow-[0_0_0_3px_rgba(39,_39,_39,_0.15)] focus-visible:text-primary hover:bg-gray-900/5 hover:text-primary',
            },
        },
        content: {
            inner: 'relative flex max-h-[90dvh] max-h-lg:max-h-max flex-col rounded-lg bg-white shadow dark:bg-gray-700',
        },
    },
}).modal;

export const Dialog = ({
    visible,
    headerText,
    footer,
    children,
    className,
    dismissible = true,
    ...props
}: DialogProps) => {
    useEffect(() => {
        document.body.classList.toggle('overflow-hidden', visible);
        return () => document.body.classList.remove('overflow-hidden');
    }, [visible]);

    return (
        <Flowbite.Modal
            show={visible}
            popup={!headerText}
            className={classNames('z-(--z-dialog)', className)}
            dismissible={dismissible}
            {...getReplaceThemeProps(modalTheme)}
            {...props}
        >
            <Flowbite.ModalHeader className="dialog-header">
                {headerText}
            </Flowbite.ModalHeader>
            <Flowbite.ModalBody className="dialog-body">
                {children}
            </Flowbite.ModalBody>
            {footer && <Flowbite.ModalFooter>{footer}</Flowbite.ModalFooter>}
        </Flowbite.Modal>
    );
};
